// ============================================================
// DM-LMS — Shared Types
// Single source of truth consumed by API and Web.
// DRY: define once here, import everywhere.
// ============================================================

// ---- Roles ----
export const ROLES = ["student", "tutor", "admin"] as const;
export type Role = (typeof ROLES)[number];

// ---- User ----
export interface User {
  id: string;
  code: string;          // ST-0001 | TU-0001 | AD-0001
  name: string;
  email: string;
  phone: string | null;
  role: Role;
  avatarUrl: string | null;
  createdAt: string;     // ISO-8601
  updatedAt: string;
}

// ---- Course ----
export const DELIVERY_MODES = ["virtual", "physical", "hybrid"] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];

export interface Course {
  id: string;
  code: string;          // CR-0001
  title: string;
  description: string;
  tutorId: string;
  mode: DeliveryMode;
  capacity: number;
  prerequisiteId: string | null;
  durationHours: number;
  priceKes: number;      // KES, integer (avoid float for money) — matches courses.price_kes
  isPublished: boolean;
  accountRef: string | null; // admin-set M-Pesa account-reference prefix, e.g. "MF"; null → falls back to code
  createdAt: string;
  updatedAt: string;
}

// ---- Course stats (GET /api/courses/:courseId/stats) ----
export const COURSE_STATUSES = ["not_started", "in_progress", "completed"] as const;
export type CourseStatus = (typeof COURSE_STATUSES)[number];

export interface CourseStats {
  totalSessions: number;
  pastSessions: number;
  upcomingSessions: number;
  totalEnrolled: number;
  completedStudents: number;
  pausedStudents: number;
  droppedStudents: number;
  courseStatus: CourseStatus;
}

// ---- Bulk course stats (GET /api/courses/stats), keyed by courseId ----
export type CourseStatsByCourseId = Record<string, CourseStats>;

// ---- Session ----
export interface Session {
  id: string;
  courseId: string;
  title: string;
  scheduledAt: string;   // ISO-8601
  durationMinutes: number;
  mode: DeliveryMode;
  meetingUrl: string | null;   // Zoom / Meet link
  venue: string | null;        // Physical location
  tutorId: string | null;      // null → session inherits the course's tutor
  recordingUrl: string | null; // R2 URL after upload
  createdAt: string;
}

// A session plus the name of its resolved tutor (its own tutorId, else the course's).
export type SessionWithTutor = Session & { tutorName: string | null };

// ---- Enrollment ----
export const PAYMENT_STATUSES = ["pending", "partial", "paid"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const ENROLLMENT_STATUSES = ["active", "paused", "completed", "dropped"] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

export interface Enrollment {
  id: string;
  studentId: string;
  courseId: string;
  paymentStatus: PaymentStatus;
  status: EnrollmentStatus;
  enrolledAt: string;
  completedAt: string | null;
}

export interface EnrollmentWithStudent extends Enrollment {
  studentName: string;
  studentCode: string;
}

// ---- Bulk enrollments-by-course snapshot (GET /api/enrollments/courses) ----
export interface EnrollmentsByCoursesResult {
  items: EnrollmentWithStudent[];
  countsByCourse: Record<string, number>;
  uniqueStudentCount: number;
}

// Enrollment row shape joined with course title (GET /api/enrollments/student/:studentId) —
// omits the lifecycle `status` field, mirroring that endpoint's select (and
// EnrollmentWithStudent's own omission of it above).
export interface EnrollmentWithCourse {
  id: string;
  studentId: string;
  courseId: string;
  paymentStatus: PaymentStatus;
  enrolledAt: string;
  completedAt: string | null;
  courseTitle: string;
}

// POST /api/enrollments response: the enrollment plus what the student needs to pay for it
export interface EnrollmentCreated extends Enrollment {
  paymentId: string | null;     // null if the best-effort pending-payment insert failed
  paymentCode: string | null;   // ditto
  courseAccountRef: string;     // course.accountRef ?? course.code — see resolveCourseAccountRef()
}

// A student's latest payment for a course, as a tutor sees it: read-only, and never the M-Pesa
// receipt code itself (only whether one was submitted).
export interface RosterPayment {
  code: string;                 // PAY-0007
  status: TransactionStatus;
  hasReference: boolean;
}

// A student's own view of their latest payment for an enrollment. Unlike RosterPayment it carries the
// code they submitted, which they may see and change, plus what MpesaInstructions needs.
export interface EnrollmentPayment {
  id: string;
  code: string;                 // PAY-0007
  status: TransactionStatus;
  reference: string | null;     // M-Pesa transaction code the student submitted, if any
  amountKes: number;
}

// GET /api/enrollments/mine row: the enrollment plus its latest payment (null = no payment record).
// Nested rather than flat — Enrollment already has a `paymentStatus` (the pending/partial/paid state).
export interface EnrollmentWithPayment extends Enrollment {
  payment: EnrollmentPayment | null;
}

// GET /api/enrollments/unpaid-count — students across a tutor's courses without a confirmed payment
export interface UnpaidStudentsSummary {
  unpaidStudents: number;
}

// Roster row: the enrollment plus that student's latest payment (null = no payment record).
// Not named `paymentStatus` — Enrollment already has one (the pending/partial/paid enrollment state).
export interface RosterEntry extends EnrollmentWithStudent {
  payment: RosterPayment | null;
}

// ---- Attendance ----
export const ATTENDANCE_STATUSES = ["present", "absent", "excused"] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

export interface AttendanceRecord {
  id: string;
  studentId: string;
  sessionId: string;
  status: AttendanceStatus;
  checkedInAt: string | null;
  notes: string | null;
}

export interface AttendanceRecordWithSession extends AttendanceRecord {
  sessionTitle: string;
  scheduledAt: string;
}

export interface AttendanceRecordWithStudent extends AttendanceRecord {
  studentName: string;
  studentCode: string;
}

// ---- Session + Course join (tutor sessions list / detail) ----
export interface SessionWithCourse extends Session {
  courseTitle: string;
}

// ---- Feedback ----
export interface FeedbackRecord {
  id: string;
  tutorId: string;
  studentId: string;
  sessionId: string | null;
  notes: string;
  isPrivate: boolean;
  createdAt: string;
}

// ---- Materials ----
export interface Material {
  id: string;
  courseId: string;
  title: string;
  r2Key: string;
  deliverAt: string;   // ISO-8601
  createdAt: string;
}

// ---- Payment ----
export const PAYMENT_METHODS = ["mpesa", "stripe", "manual"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const TRANSACTION_STATUSES = ["pending", "success", "failed", "refunded"] as const;
export type TransactionStatus = (typeof TRANSACTION_STATUSES)[number];

export interface Payment {
  id: string;
  code: string;             // PAY-0001
  enrollmentId: string;
  studentId: string;
  amountKes: number;        // Always in KES, integer
  method: PaymentMethod;
  status: TransactionStatus;
  reference: string | null; // M-Pesa receipt / Stripe PI ID
  paidAt: string | null;
  createdAt: string;
  courseId: string | null;  // null on rows created before migration 0004
  updatedAt: string | null;
}

// Payment history row: a payment plus its course's title and resolved account-ref prefix
// (both null when courseId is null)
export interface PaymentWithCourse extends Payment {
  courseTitle: string | null;
  courseAccountRef: string | null;   // course.accountRef ?? course.code
}

// Proof of payment, recorded when an admin confirms a payment. ADMIN-ONLY: never part of `Payment` or
// `PaymentWithCourse`, and never selected by a student-facing query. All null until the payment is confirmed
// (and for payments confirmed before proof existed, except type/reference which migration 0012 backfilled).
export interface PaymentProof {
  proofType:        string | null;   // a proof_type config key; "cash" is PROOF_TYPE_CASH
  proofReference:   string | null;   // normalised; null for cash
  proofPayer:       string | null;   // cash only
  proofReceiver:    string | null;   // cash only
  proofAmountKes:   number | null;
  confirmedBy:      string | null;   // admin user id
}

// A payment as an admin sees it right after settling it
export interface PaymentWithProof extends Payment, PaymentProof {}

// Admin ledger row: adds the student's code (never name/email — PII stays out of list views) and the proof
export interface AdminPayment extends PaymentWithCourse, PaymentProof {
  studentCode: string;
}

// PATCH /api/payments/:id/verify result. Both outcomes are HTTP 200 — `match` says which one.
export interface PaymentVerification {
  match: boolean;
  payment: PaymentWithProof;
  message?: string;         // set on mismatch, shows both codes
}

// ---- Certificate ----
export interface Certificate {
  id: string;
  code: string;             // CERT-0001
  studentId: string;
  courseId: string;
  issuedAt: string;
  r2Key: string;            // R2 storage key — never expose full URL directly
  downloadUrl: string | null; // Signed URL, generated on demand
}

// ---- System Config ----
export const CONFIG_CATEGORIES = [
  "profession",
  "specialisation",
  "county",
  "course_mode",
  "payment_method",
  "mpesa_config",
  "admin_email",
  "session_duration",
  "session_venue",
  "tutor_email",
  "proof_type",
] as const;
export type ConfigCategory = (typeof CONFIG_CATEGORIES)[number];

// Categories whose active options hold personal data: GET /api/config is admin-only for these.
export const ADMIN_ONLY_CONFIG_CATEGORIES = ["tutor_email", "admin_email", "proof_type"] as const satisfies readonly ConfigCategory[];

export interface SystemConfig {
  id: string;
  category: ConfigCategory;
  key: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  metadata: string | null;   // JSON-encoded string
  createdAt: string;         // ISO-8601
  updatedAt: string;
}

export interface ConfigOption {
  key: string;
  label: string;
  metadata?: Record<string, unknown>;
}

// ---- Student Profile ----
export const PROFESSIONS = ["advocate", "hr_professional", "court_staff", "mediator", "other"] as const;
export type Profession = (typeof PROFESSIONS)[number];

export const PREFERRED_PAYMENT_METHODS = ["mpesa", "bank_transfer", "card", "other"] as const;
export type PreferredPaymentMethod = (typeof PREFERRED_PAYMENT_METHODS)[number];

export interface StudentProfile {
  id: string;
  userId: string;
  phone: string | null;
  idNumber: string | null;
  organization: string | null;
  profession: Profession | null;
  country: string | null;   // ISO 3166-1 alpha-2 code
  county: string | null;     // Kenya only
  region: string | null;     // free text, outside Kenya
  preferredPaymentMethod: PreferredPaymentMethod | null;
  mpesaNumber: string | null;
  bio: string | null;
  profileComplete: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---- Tutor Profile ----
export const SPECIALISATIONS = ["commercial", "family", "labour", "community", "other"] as const;
export type Specialisation = (typeof SPECIALISATIONS)[number];

export const TUTOR_PROFILE_STATUSES = ["pending", "active", "rejected"] as const;
export type TutorProfileStatus = (typeof TUTOR_PROFILE_STATUSES)[number];

export interface TutorProfile {
  id: string;
  userId: string;
  phone: string | null;
  specialisation: Specialisation | null;
  qualifications: string | null;
  bio: string | null;
  photoR2Key: string | null;
  status: TutorProfileStatus;
  rejectionReason: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---- Activity Log ----
export const ACTIVITY_EVENT_TYPES = [
  "admin.created",
  "student.registered",
  "enrollment.created",
  "payment.received",
  "payment.confirmed",
  "payment.failed",
  "certificate.issued",
  "tutor.applied",
  "tutor.approved",
  "tutor.auto_created",
  "tutor.rejected",
  "session.created",
  "course.updated",
  "attendance.checked_in",
  "student.profile_completed",
  "user.deleted",
] as const;
export type ActivityEventType = (typeof ACTIVITY_EVENT_TYPES)[number];

export const ACTIVITY_ENTITY_TYPES = [
  "enrollment",
  "payment",
  "certificate",
  "session",
  "course",
  "attendance",
  "user",
  "profile",
] as const;
export type ActivityEntityType = (typeof ACTIVITY_ENTITY_TYPES)[number];

export interface ActivityLog {
  id: string;
  actorId: string;
  actorRole: Role;
  eventType: ActivityEventType;
  entityId: string | null;
  entityType: ActivityEntityType | null;
  description: string;
  metadata: string | null;   // JSON string, opaque to the DB layer
  createdAt: string;
}

export interface CreateActivityLogInput {
  actorId: string;
  actorRole: Role;
  eventType: ActivityEventType;
  entityId?: string | null;
  entityType?: ActivityEntityType | null;
  description: string;
  metadata?: string | null;
}

// ---- Admin Snapshot ----
export interface EnrollmentsByMonth {
  month: string;   // "YYYY-MM"
  count: number;
}

export interface AdminSnapshot {
  totalStudents: number;
  totalTutors: number;
  totalCourses: number;
  publishedCourses: number;
  totalEnrollments: number;
  pendingPayments: number;
  pendingCertificates: number;   // paid enrollments not yet completed
  totalRevenueKes: number;
  certificatesIssued: number;
  enrollmentsByMonth: EnrollmentsByMonth[];
}

// ---- API Response envelope ----
export interface ApiSuccess<T> {
  ok: true;
  data: T;
}

export interface ApiError {
  ok: false;
  error: string;
  code: string;
  statusCode: number;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

// ---- Pagination ----
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

// ---- Admin: Users list and per-role detail ----
// GET /api/users rows. The extras are present only for the matching role filter.
export interface StudentListExtras {
  coursesEnrolled: number;
  worstPaymentStatus: PaymentStatus | null;
}

export interface TutorListExtras {
  specialisation: Specialisation | null;   // null when the tutor has no profile row
  coursesCount: number;
  studentsCount: number;
}

export type AdminUserListItem = User & Partial<StudentListExtras> & Partial<TutorListExtras>;

// The role profile as GET /api/users/:id returns it to an admin viewing another user.
export type StudentProfileForAdmin = Omit<StudentProfile, "idNumber" | "mpesaNumber">;
export type TutorProfileForAdmin = Omit<TutorProfile, "photoR2Key" | "rejectionReason">;

// GET /api/users/:id as an admin sees it, discriminated by role so a page can narrow without casts.
export type AdminUserRecord =
  | (User & { role: "student"; profile: StudentProfileForAdmin | null })
  | (User & { role: "tutor"; profile: TutorProfileForAdmin | null })
  | (User & { role: "admin"; profile: null });

// GET /api/admin/users/:id/tutor-detail
export type TutorDetailProfile = Pick<TutorProfile, "status" | "specialisation" | "phone" | "qualifications" | "bio">;

export interface TutorDetailCourse {
  code: string;
  title: string;
  mode: DeliveryMode;
  isPublished: boolean;
  enrolledCount: number;
}

export interface TutorDetailSession {
  title: string;
  scheduledAt: string;
  courseCode: string;
}

export interface TutorDetailStudent {
  code: string;
  name: string;
}

export interface TutorDetail {
  id: string;
  code: string;
  name: string;
  email: string;
  createdAt: string;
  profile: TutorDetailProfile | null;
  courses: TutorDetailCourse[];
  sessions: TutorDetailSession[];
  students: TutorDetailStudent[];
}

// GET /api/admin/users/:id/admin-detail
export type AdminActivityPreview = Pick<
  ActivityLog,
  "id" | "eventType" | "description" | "entityType" | "entityId" | "createdAt"
>;

export interface AdminDetail {
  id: string;
  code: string;
  name: string;
  email: string;
  createdAt: string;
  recentActivity: AdminActivityPreview[];
}
